import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import clsx from 'clsx';
import MUIDataTable from 'mui-datatables';
import {
  Divider,
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  FormControl,
  Select,
  MenuItem,
} from '@material-ui/core';
import {
  AddRounded as AddRoundedIcon,
  Clear as ClearIcon,
} from '@material-ui/icons';

import { SectionHeader } from 'components';
import { Context } from 'context';
import { PlansContext } from 'Views/Plans/context';
import {
  UPDATE_PAYMENT_CATEGORY,
} from 'Views/Plans/context/actions';
import { isSubscribiliUser } from 'Shared/Utils';
import { LEDGER_PROVIDER_MODE_LIST } from 'Views/Common/enum';
import useStyles from './styles';


const NEW_PAYMENT_SERVICE = {
  cpt_code: '',
  cpt_description: '',
  category: '',
  ledger_provider_mode: 'appointment_provider',
};


const SubscriptionPaymentCodes = (props) => {

  const {
    state: { user },
  } = useContext(Context);

  const {
    state: {
      highlightErrors,
      discounts = {},
      subs_payment_codes = [],
    },
    dispatch,
  } = useContext(PlansContext);

  const subscribiliUser = isSubscribiliUser(user);

  const {
    formValid = true,
  } = discounts;

  const classes = useStyles();


  // --- Handlers ---

  const onPaymentFieldChange = (evt, dIndex) => {
    const { name: dName, value: dValue } = evt?.target || {};
    let parsedValue = dValue;

    if (dIndex !== undefined && Array.isArray(subs_payment_codes)) {
      const clonedValue = _.cloneDeep(subs_payment_codes);
      if (!clonedValue[dIndex]) return;
      clonedValue[dIndex][dName] = parsedValue;
      updateCategoryValues({
        payment_codes: clonedValue,
      });
    }
  };

  const onProviderModeChange = (evt, dIndex) => {
    const mode = evt?.target?.value;
    const clonedValue = _.cloneDeep(subs_payment_codes);
    if (!clonedValue[dIndex]) return;
    clonedValue[dIndex].ledger_provider_mode = mode;
    clonedValue[dIndex].ledger_provider_name =
      mode === 'default_provider'
        ? clonedValue[dIndex].ledger_provider_name || ''
        : null;
    updateCategoryValues({ payment_codes: clonedValue });
  };

  const addPaymentCode = () => {
    let clonedList = _.cloneDeep(subs_payment_codes);
    clonedList.push(NEW_PAYMENT_SERVICE);
    updateCategoryValues({
      payment_codes: clonedList,
    });
  };

  const deletePaymentCode = (delId) => {
    let clonedList = _.cloneDeep(subs_payment_codes);
    clonedList.splice(delId, 1);
    updateCategoryValues({
      payment_codes: clonedList,
    });
  };

  const updateCategoryValues = (updatedValues) =>
    dispatch({
      type: UPDATE_PAYMENT_CATEGORY,
      payload: {
        payment_codes:updatedValues.payment_codes || [],
      },
    });

  // --- Table Columns ---

  const xsmallCellProps = () => ({ className: classes.xsmallColumn });
  const mediumCellProps = () => ({ className: classes.mediumColumn });
  const largeCellProps = () => ({ className: classes.largeColumn });
  const topAlignCellProps = () => ({ style: { verticalAlign: 'top' } });

  const errorForm = Boolean(highlightErrors && !formValid);

  const renderTextInput = (value, name, rowId, props = {}, inputProps = {}) => (
    <TextField
      required
      fullWidth
      variant="outlined"
      margin="dense"
      value={value === '-' ? '' : value}
      name={name}
      inputProps={{
        'data-testid': `category-input-${name}-${rowId}`,
        ...inputProps,
      }}
      {...props}
    />
  );

  const noLifeTimeTableColumns = [
    {
      name: 'is_display',
      label: ' ',
      options: {
        setCellProps: topAlignCellProps,
        setCellHeaderProps: xsmallCellProps,
        customBodyRender: (_value, tableMetaData) => {
          const { rowIndex } = tableMetaData;
          const showAddButton = rowIndex + 1 >= subs_payment_codes.length;
          if (!showAddButton) return <></>;
          return (
            <IconButton
              color="secondary"
              title="Add Custom Code"
              aria-label="add-procedure-code"
              onClick={addPaymentCode}
              data-testid={`add-procedure-code-button-${rowIndex}`}
              id={'category-discounts-services'}
            >
              <AddRoundedIcon fontSize="inherit" />
            </IconButton>
          );
        },
      },
    },
    {
      name: 'category',
      label: 'Category',
      options: {
        setCellProps: topAlignCellProps,
        setCellHeaderProps: mediumCellProps,
        customBodyRender: (value, tableMetaData) => {
          const { rowIndex } = tableMetaData;
          let disabled = subscribiliUser;
          return renderTextInput(
            value,
            'category',
            rowIndex,
            {
              onChange: (evt) => {
                onPaymentFieldChange(evt, rowIndex);
              },
              error: errorForm && !value,
            },
            {
              disabled,
            },
          );
        },
      },
    },
    {
      name: 'cpt_code',
      label: 'CPT Code/Range',
      options: {
        setCellProps: topAlignCellProps,
        setCellHeaderProps: mediumCellProps,
        customBodyRender: (value, tableMetaData) => {
          const { rowIndex } = tableMetaData;
          let disabled = subscribiliUser;
          return renderTextInput(
            value,
            'cpt_code',
            rowIndex,
            {
              onChange: (evt) => {
                onPaymentFieldChange(evt, rowIndex);
              },
              error: errorForm && !value,
            },
            {
              disabled,
            },
          );
        },
      },
    },
    {
      name: 'cpt_description',
      label: 'Description',
      options: {
        setCellProps: topAlignCellProps,
        setCellHeaderProps: largeCellProps,
        customBodyRender: (value, tableMetaData) => {
          const { rowIndex } = tableMetaData;
          let disabled = subscribiliUser;
          return renderTextInput(
            value,
            'cpt_description',
            rowIndex,
            {
              onChange: (evt) => {
                onPaymentFieldChange(evt, rowIndex);
              },
              error: errorForm && !value,
            },
            {
              disabled,
            },
          );
        },
      },
    },
    {
      name: 'ledger_provider_mode',
      label: 'Provider',
      options: {
        setCellProps: topAlignCellProps,
        setCellHeaderProps: mediumCellProps,
        customBodyRender: (value, tableMetaData) => {
          const { rowIndex } = tableMetaData;
          const providerName = subs_payment_codes[rowIndex]?.ledger_provider_name;
          const nameError = Boolean(highlightErrors && !providerName?.trim());
          // Note: only the first payment code entry is read when posting,
          // so Custom Provider is offered on the first row only.
          const choices = Object.keys(LEDGER_PROVIDER_MODE_LIST).filter(
            (modeKey) =>
              rowIndex === 0 || modeKey !== 'default_provider',
          );
          return (
            <div className={classes.providerCell}>
              <FormControl fullWidth variant="outlined" margin="dense">
                <Select
                  id={`payment-code-provider-select-${rowIndex}`}
                  name="ledger_provider_mode"
                  value={value || ''}
                  disabled={subscribiliUser}
                  onChange={(evt) => onProviderModeChange(evt, rowIndex)}
                  inputProps={{
                    'data-testid': `payment-code-provider-select-${rowIndex}`,
                  }}
                >
                  {choices.map((modeKey) => (
                    <MenuItem
                      key={modeKey}
                      value={modeKey}
                      data-testid={`payment-code-provider-option-${rowIndex}-${modeKey}`}
                    >
                      {LEDGER_PROVIDER_MODE_LIST[modeKey]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {value === 'default_provider' &&
                renderTextInput(
                  providerName || '',
                  'ledger_provider_name',
                  rowIndex,
                  {
                    placeholder: 'Provider abbreviation',
                    helperText: nameError
                      ? '* Enter the provider abbreviation for Custom Provider'
                      : 'Custom Provider is currently supported only for Open Dental.',
                    onChange: (evt) => onPaymentFieldChange(evt, rowIndex),
                    error: nameError,
                  },
                  {
                    disabled: subscribiliUser,
                    maxLength: 100,
                  },
                )}
            </div>
          );
        },
      },
    },
    {
      name: '',
      options: {
        setCellProps: topAlignCellProps,
        setCellHeaderProps: mediumCellProps,
        customBodyRender: (_value, tableMetaData) => {
          const { rowIndex } = tableMetaData;
          return (
            <div className="d-flex flex-no-wrap">
              {rowIndex >= 0 && (
                <IconButton
                  aria-label="delete"
                  onClick={() => {
                    deletePaymentCode(rowIndex);
                  }}
                  data-testid={`delete-procedure-button-${rowIndex}`}
                  disabled={subscribiliUser}
                >
                  <ClearIcon fontSize="inherit" />
                </IconButton>
              )}
            </div>
          );
        },
      },
    },
  ];

  return (
    <div
      id="subscription-payment-codes-table"
      className={clsx('widget-wrapper')}
      elevation={0}
    >
      <Grid container spacing={2} id="category-discounts-table">
        <Grid item xs={12}>
          <Grid item xs={12} alignItems="center" justifyContent="space-between">
            <SectionHeader
              title="Subscription Payment Codes"
              titleVariant="h5"
              titleComponent="h5"
              titleJustify="space-between"
              rightSection={
                <Button
                  startIcon={<AddRoundedIcon fontSize="large" />}
                  size="large"
                  color="secondary"
                  onClick={addPaymentCode}
                  data-testid="add-procedure-code-button"
                  disabled={subscribiliUser}
                >
                  Add Custom Code
                </Button>
              }
            />
            <Divider />
          </Grid>
          <MUIDataTable
            data={subs_payment_codes}
            columns={noLifeTimeTableColumns}
            options={{
              filter: false,
              sort: false,
              selectableRows: 'none',
              responsive: 'standard',
              print: false,
              pagination: true,
              search: false,
              download: false,
              viewColumns: false,
              elevation: 0,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Grid
            container
            spacing={0}
            alignItems="center"
            justifyContent={'flex-end'}
          >
            {/* Save Changes Button and Error Message can be added here if needed */}
            <Grid item>
              {errorForm && (
                <Typography className={classes.errorText} color="error">
                  * Please fill the required fields
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

SubscriptionPaymentCodes.propTypes = {
  level: PropTypes.string,
  levelId: PropTypes.number,
  planPackageId: PropTypes.number,
  otherButtons: PropTypes.string,
  viewOnly: PropTypes.bool,
  setExecuteHandleSubmit: PropTypes.func,
};

export default SubscriptionPaymentCodes;
